import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { utils, toast, localize } from '@easygenerator/fragment-sdk';
import { LoggingType } from '@easygenerator/fragment-sdk/dist/utils/logger/constants';
import Config from '../config';
import { IProfileMeta, IUser } from '../types/types';

const config = Config.getInstance();
const logger = utils.Logger.getInstance();
const HttpWrapper = utils.HttpWrapper;

const SET_USER_DATA = 'SET_USER_DATA';
const UPDATE_NAME = 'UPDATE_NAME';
const UPDATE_PROFILE_META = 'UPDATE_PROFILE_META';
const UPDATE_AVATAR_URL = 'UPDATE_AVATAR_URL';

export class State {
  user: IUser = {
    id: '',
    name: '',
    email: '',
    avatarUrl: '',
    country: '',
    city: ''
  };
}

const getters = {
  user: state => state.user
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as GetterTree<State, any>;

const mutations = {
  [SET_USER_DATA](state: State, user: IUser) {
    state.user = { ...user };
  },
  [UPDATE_NAME](state: State, { name }: { name: string }) {
    state.user.name = name;
  },
  [UPDATE_PROFILE_META](state: State, profileMeta: IProfileMeta) {
    state.user = { ...state.user, ...profileMeta };
  },
  [UPDATE_AVATAR_URL](state, newAvatarUrl) {
    state.user = { ...state.user, avatarUrl: newAvatarUrl };
  }
} as MutationTree<State>;

const actions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async initUser({ commit }: ActionContext<State, any>, token: string) {
    try {
      const user: IUser | null = await HttpWrapper.get(`${config.learnServiceUrl}/api/learner/me`, {
        bearerToken: token
      });
      if (user === null) {
        logger.trackError(LoggingType.error, 'the function initUser got null on request');
        return;
      }
      commit(SET_USER_DATA, user);
      this.commit('app/SET_USER_LOADING', false, { root: true });
    } catch (error) {
      logger.trackError(LoggingType.error, error);
    }
  },
  async updateName(
    {
      commit,
      rootState: {
        app: { token }
      }
    },
    { name }: { name: string }
  ) {
    try {
      await HttpWrapper.put(`${config.authServiceUrl}/api/account/name`, {
        bearerToken: token,
        body: { name },
        fetchOptions: { credentials: 'include' }
      });
      commit(UPDATE_NAME, { name });
    } catch (error) {
      logger.trackError(LoggingType.error, error);
    }
  },
  updateAvatarUrl({ commit }, newAvatarUrl) {
    commit('UPDATE_AVATAR_URL', newAvatarUrl);
  },
  async updateProfile(
    {
      commit,
      rootState: {
        app: { token }
      }
    },
    profile: IProfileMeta
  ): Promise<boolean> {
    try {
      await HttpWrapper.put(`${config.authServiceUrl}/api/account/profile`, {
        bearerToken: token,
        body: profile,
        fetchOptions: { credentials: 'include' }
      });
      commit(UPDATE_PROFILE_META, profile);
      toast.success(localize('changesWereSuccessfullySaved'));

      return true;
    } catch (error) {
      logger.trackError(LoggingType.error, error);
      toast.error((error as Error).message);

      return false;
    }
  },
  async deleteAccount(
    {
      rootState: {
        app: { token }
      }
    },
    { password }: { password: string }
  ) {
    await HttpWrapper.post(`${config.authServiceUrl}/api/account/delete`, {
      bearerToken: token,
      body: { password, clientId: 'learn' },
      fetchOptions: { credentials: 'include' }
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as ActionTree<State, any>;

export default {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions
};
